import { language as baseJsLanguage } from 'monaco-editor/esm/vs/basic-languages/javascript/javascript.js';
import { language as basePythonLanguage } from 'monaco-editor/esm/vs/basic-languages/python/python.js';

/** Настоящие ключевые слова Python (без встроенных функций). */
const PYTHON_KEYWORDS = new Set([
  'False',
  'None',
  'True',
  '_',
  'and',
  'as',
  'assert',
  'async',
  'await',
  'break',
  'case',
  'class',
  'continue',
  'def',
  'del',
  'elif',
  'else',
  'except',
  'finally',
  'for',
  'from',
  'global',
  'if',
  'import',
  'in',
  'is',
  'lambda',
  'match',
  'nonlocal',
  'not',
  'or',
  'pass',
  'raise',
  'return',
  'try',
  'type',
  'while',
  'with',
  'yield',
]);

/** Встроенные функции Python — подсветка predefined, не keyword. */
export const PYTHON_BUILTIN_FUNCTIONS = new Set(
  basePythonLanguage.keywords.filter((word) => !PYTHON_KEYWORDS.has(word)),
);

/** Глобальные встроенные объекты/функции JavaScript. */
export const JS_GLOBAL_BUILTINS = new Set([
  'Array',
  'ArrayBuffer',
  'BigInt',
  'Boolean',
  'DataView',
  'Date',
  'Error',
  'EvalError',
  'Float32Array',
  'Float64Array',
  'Function',
  'Infinity',
  'Int16Array',
  'Int32Array',
  'Int8Array',
  'Intl',
  'JSON',
  'Map',
  'Math',
  'NaN',
  'Number',
  'Object',
  'Promise',
  'Proxy',
  'RangeError',
  'ReferenceError',
  'Reflect',
  'RegExp',
  'Set',
  'String',
  'Symbol',
  'SyntaxError',
  'TypeError',
  'URIError',
  'Uint16Array',
  'Uint32Array',
  'Uint8Array',
  'Uint8ClampedArray',
  'WeakMap',
  'WeakSet',
  'console',
  'decodeURI',
  'decodeURIComponent',
  'encodeURI',
  'encodeURIComponent',
  'escape',
  'eval',
  'isFinite',
  'isNaN',
  'parseFloat',
  'parseInt',
  'unescape',
]);

type MonarchRule = unknown;

type MonacoLanguagesApi = {
  languages: {
    setMonarchTokensProvider: (languageId: string, languageDefinition: unknown) => void;
  };
};

let sciplexCodeLanguagesConfigured = false;

function patchIdentifierCases(rule: MonarchRule, builtinToken = 'predefined'): MonarchRule {
  if (!Array.isArray(rule) || rule.length < 2) return rule;

  const actions = rule[1];
  if (!actions || typeof actions !== 'object' || !('cases' in actions)) return rule;

  const cases = (actions as { cases: Record<string, string> }).cases;
  if (!('@keywords' in cases)) return rule;

  return [
    rule[0],
    {
      ...(actions as object),
      cases: {
        '@builtinFunctions': builtinToken,
        ...cases,
      },
    },
  ];
}

function patchTokenizerState(
  rules: MonarchRule[],
  builtinToken = 'predefined',
): MonarchRule[] {
  return rules.map((rule) => patchIdentifierCases(rule, builtinToken));
}

export function configureSciplexCodeLanguages(monaco: MonacoLanguagesApi): void {
  if (sciplexCodeLanguagesConfigured) return;
  sciplexCodeLanguagesConfigured = true;

  const pythonKeywords = basePythonLanguage.keywords.filter((word) => PYTHON_KEYWORDS.has(word));
  const pythonBuiltins = [...PYTHON_BUILTIN_FUNCTIONS];

  monaco.languages.setMonarchTokensProvider('python', {
    ...basePythonLanguage,
    keywords: pythonKeywords,
    builtinFunctions: pythonBuiltins,
    tokenizer: {
      ...basePythonLanguage.tokenizer,
      root: patchTokenizerState(basePythonLanguage.tokenizer.root),
    },
  });

  const jsBuiltins = [...JS_GLOBAL_BUILTINS];

  monaco.languages.setMonarchTokensProvider('javascript', {
    ...baseJsLanguage,
    builtinFunctions: jsBuiltins,
    tokenizer: {
      ...baseJsLanguage.tokenizer,
      common: patchTokenizerState(baseJsLanguage.tokenizer.common),
    },
  });
}
